import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "@ghezaa/shell";
import {
  inMarket,
  query,
  type AvailableMarket,
  type BcCompany,
  type BcStatus,
  type CustomerDetail,
  type CustomerFigures,
  type CustomerRow,
  type DataGaps,
  type LogRow,
  type MarketsAnswer,
  type MeInMarket,
  type Page,
  type PersonRef,
  type ReportCatalogue,
  type ReportDimension,
  type ReportSpec,
  type ReportTable,
  type ReportValue,
  type ReportView,
  type ViewsAnswer,
  type SalespersonRow,
  type TargetsAnswer,
  type TeamAnswer,
  type TodayAnswer,
} from "./client";

export function useMarkets() {
  return useQuery({ queryKey: ["markets"], queryFn: () => api<MarketsAnswer>("/api/markets") });
}

export function useAvailableMarkets(enabled: boolean) {
  return useQuery({ queryKey: ["markets", "available"], queryFn: () => api<AvailableMarket[]>("/api/markets/available"), enabled });
}

export function useMe(market: string) {
  return useQuery({ queryKey: ["me", market], queryFn: () => api<MeInMarket>(`${inMarket(market)}/me`) });
}

export type CustomerParams = { q?: string; salesperson?: string; silent?: boolean; sort?: string; cursor?: string; take: number };

export function useCustomers(market: string, params: CustomerParams) {
  return useQuery({
    queryKey: ["customers", market, params],
    queryFn: () => api<Page<CustomerRow>>(`${inMarket(market)}/customers${query(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useCustomer(market: string, no: string) {
  return useQuery({ queryKey: ["customer", market, no], queryFn: () => api<CustomerDetail>(`${inMarket(market)}/customers/${encodeURIComponent(no)}`) });
}

export function useCustomerFigures(market: string, no: string) {
  return useQuery({ queryKey: ["customer", market, no, "figures"], queryFn: () => api<CustomerFigures>(`${inMarket(market)}/customers/${encodeURIComponent(no)}/figures`) });
}

export function useToday(market: string) {
  return useQuery({ queryKey: ["today", market], queryFn: () => api<TodayAnswer>(`${inMarket(market)}/today`) });
}

/** The team's month (`2026-10`); the current one when none is given. */
export function useTeam(market: string, month: string | undefined) {
  return useQuery({ queryKey: ["team", market, month ?? ""], queryFn: () => api<TeamAnswer>(`${inMarket(market)}/team${query({ month })}`), placeholderData: keepPreviousData });
}

/** A month's targets (`2026-12`); this month when none is given. */
export function useTargets(market: string, month: string | undefined) {
  return useQuery({ queryKey: ["targets", market, month ?? ""], queryFn: () => api<TargetsAnswer>(`${inMarket(market)}/targets${query({ month })}`), placeholderData: keepPreviousData });
}

/** The report's address in the API: its definition travels as JSON in the query, so a report is a link. */
export const reportPath = (market: string, spec: ReportSpec, kind: "run" | "export" = "run", extra: Record<string, string> = {}) =>
  `${inMarket(market)}/reports/${kind}${query({ ...extra, spec: JSON.stringify(spec) })}`;

export function useReportCatalogue(market: string) {
  return useQuery({ queryKey: ["report-catalogue", market], queryFn: () => api<ReportCatalogue>(`${inMarket(market)}/reports/catalogue`), staleTime: Infinity });
}

export function useReport(market: string, spec: ReportSpec | null) {
  return useQuery({
    queryKey: ["report", market, spec ? JSON.stringify(spec) : ""],
    queryFn: () => api<ReportTable>(reportPath(market, spec!)),
    enabled: spec !== null,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useReportViews(market: string) {
  return useQuery({ queryKey: ["report-views", market], queryFn: () => api<ViewsAnswer>(`${inMarket(market)}/reports/views`) });
}

export function useReportView(market: string, id: string | undefined) {
  return useQuery({
    queryKey: ["report-views", market, id],
    queryFn: () => api<ReportView>(`${inMarket(market)}/reports/views/${encodeURIComponent(id!)}`),
    enabled: id !== undefined,
    retry: false,
  });
}

/** What a filter on a dimension can choose, within the person's books; narrowed by `q` for customers and items. */
export function useReportValues(market: string, dimension: ReportDimension | null, q: string) {
  return useQuery({
    queryKey: ["report-values", market, dimension, q],
    queryFn: () => api<ReportValue[]>(`${inMarket(market)}/reports/values${query({ dimension: dimension ?? undefined, q })}`),
    enabled: dimension !== null,
    placeholderData: keepPreviousData,
  });
}

export function useDataGaps(market: string) {
  return useQuery({ queryKey: ["data-gaps", market], queryFn: () => api<DataGaps>(`${inMarket(market)}/data-gaps`) });
}

export function usePeople(market: string, enabled = true) {
  return useQuery({ queryKey: ["people", market], queryFn: () => api<SalespersonRow[]>(`${inMarket(market)}/people`), enabled });
}

export function usePeopleSearch(q: string) {
  return useQuery({
    queryKey: ["people-search", q],
    queryFn: () => api<PersonRef[]>(`/api/people/search${query({ q })}`),
    enabled: q.length >= 2,
    placeholderData: keepPreviousData,
  });
}

export function useBcStatus(market: string, enabled = true) {
  return useQuery({ queryKey: ["bc", market], queryFn: () => api<BcStatus>(`${inMarket(market)}/business-central`), enabled });
}

export function useBcCompanies(market: string, enabled: boolean) {
  return useQuery({ queryKey: ["bc", market, "companies"], queryFn: () => api<BcCompany[]>(`${inMarket(market)}/business-central/companies`), enabled, retry: false });
}

export type LogParams = { q?: string; market?: string; cursor?: string; take: number };

export function useLog(params: LogParams) {
  return useQuery({ queryKey: ["log", params], queryFn: () => api<Page<LogRow>>(`/api/log${query(params)}`), placeholderData: keepPreviousData });
}
